import { Injectable } from '@angular/core';
import { AngularFirestore, QueryFn } from '@angular/fire/compat/firestore';
import { Timestamp } from 'firebase/firestore';
import { firstValueFrom } from 'rxjs';

import UniqueEntityID from '../../../core/domain/unique_entity_id';
import { PeriodBarResult } from '../../../ui/components/period-bar/period-bar.component';
import { Patient, PatientProps } from '../../patient/domain/patient';
import { PredefinedTask } from '../domain/predefined-task';
import { Task } from '../domain/task';
import { TaskAction, TaskTrigger, ToDo, ToDoStatus } from '../domain/todo';
import {
  PredefinedTaskRepository,
  PredefinedTaskSchema,
} from './predefined-task.repository';
import { TaskRepository, TaskSchema } from './task.repository';

export interface ToDoTargetSchema {
  id: string;
  firstName: string;
  lastName: string;
}

export interface ToDoSchema {
  task: string;
  taskTitle: string | null;
  dateStart: Timestamp;
  dateEnd: Timestamp | null;
  description: string;
  target: ToDoTargetSchema | ToDoTargetSchema[] | null;
  triggeredBy: TaskTrigger | null;
  actions: TaskAction[] | null;
  status: ToDoStatus;
  createdAt: Timestamp;
  updatedAt: Timestamp | null;
}

@Injectable()
export class ToDoRepository {
  constructor(private firestore: AngularFirestore) {
    // Nada
  }

  private collection(dietitianId: string, queryFn?: QueryFn) {
    return this.firestore
      .collection('dietitians')
      .doc(dietitianId)
      .collection<ToDoSchema>('todos', queryFn);
  }

  fromTargetSchema(
    schema: ToDoTargetSchema | ToDoTargetSchema[] | null,
  ): Patient | Patient[] | undefined {
    if (schema) {
      if (Array.isArray(schema)) {
        return schema
          .filter((item) => !!item)
          .map((item) => this.fromTargetSchema(item)) as Patient[];
      } else {
        return Patient.create(
          {
            firstName: schema.firstName,
            lastName: schema.lastName,
          } as PatientProps,
          new UniqueEntityID(schema.id),
        );
      }
    }
    return undefined;
  }

  async fromSchema(
    schema: ToDoSchema,
    dietitianId: string,
    id?: string,
  ): Promise<ToDo> {
    const task = await this.fromTaskPath(schema.task);
    return ToDo.create(
      {
        dietitianId,
        task,
        taskTitle: schema.taskTitle ?? undefined,
        status: schema.status,
        target: this.fromTargetSchema(schema.target),
        dateStart: schema.dateStart.toDate(),
        dateEnd: schema.dateEnd?.toDate() ?? undefined,
        description: schema.description,
        triggeredBy: schema.triggeredBy ?? undefined,
        actions: schema.actions ?? undefined,
        createdAt: schema.createdAt.toDate(),
        updatedAt: schema.updatedAt?.toDate() ?? undefined,
      },
      new UniqueEntityID(id),
    );
  }

  private async fromTaskPath(taskPath: string): Promise<Task | PredefinedTask> {
    const task = await firstValueFrom(this.firestore.doc(taskPath).get());
    const data = task.data();
    if (data) {
      const id = task.id;
      if (
        taskPath.indexOf('dietitians') >= 0 &&
        taskPath.indexOf('tasks') >= 0
      ) {
        // Task
        const paths = taskPath.split('/');
        return TaskRepository.fromSchema(data as TaskSchema, paths[1], id);
      } else if (taskPath.indexOf('predefined_tasks') >= 0) {
        // PredefinedTask
        return PredefinedTaskRepository.fromSchema(
          data as PredefinedTaskSchema,
          id,
        );
      }
    }
    throw new Error('DocumentRef not found');
  }

  toSchema(entity: ToDo): ToDoSchema {
    const task = this.toTaskPath(entity.task);
    return <ToDoSchema>{
      task,
      taskTitle: entity.taskTitle ?? null,
      status: entity.status,
      target: this.toTargetSchema(entity.target),
      dateStart: Timestamp.fromDate(entity.dateStart),
      dateEnd: entity.dateEnd ? Timestamp.fromDate(entity.dateEnd) : null,
      description: entity.description,
      triggeredBy: entity.triggeredBy ?? null,
      actions: entity.actions ?? null,
      createdAt: entity.createdAt
        ? Timestamp.fromDate(entity.createdAt)
        : Timestamp.now(),
      updatedAt: entity.updatedAt ? Timestamp.fromDate(entity.updatedAt) : null,
    };
  }

  private toTargetSchema(
    entity: Patient | Patient[] | undefined,
  ): ToDoTargetSchema | ToDoTargetSchema[] | null {
    if (entity) {
      if (Array.isArray(entity)) {
        return entity
          .filter((item) => !!item)
          .map((item) => this.toTargetSchema(item)) as ToDoTargetSchema[];
      } else {
        return <ToDoTargetSchema>{
          id: entity.id.toString(),
          firstName: entity.firstName,
          lastName: entity.lastName,
        };
      }
    }
    return null;
  }

  private toTaskPath(task: Task | PredefinedTask): string {
    if (task instanceof Task) {
      return 'dietitians/' + task.dietitianId + '/tasks/' + task.id.toString();
    } else {
      return 'predefined_tasks/' + task.id.toString();
    }
  }

  async findAll(dietitianId: string, status?: ToDoStatus): Promise<ToDo[]> {
    let docs;
    if (status) {
      const snap = await firstValueFrom(
        this.collection(dietitianId, (ref) =>
          ref.where('status', '==', status),
        ).get(),
      );
      docs = snap.docs;
    } else {
      const snap = await firstValueFrom(this.collection(dietitianId).get());
      docs = snap.docs;
    }
    const results: ToDo[] = [];
    for (const doc of docs) {
      results.push(
        await this.fromSchema(doc.data() as ToDoSchema, dietitianId, doc.id),
      );
    }
    return results;
  }

  async findAllInStatus(
    dietitianId: string,
    period: PeriodBarResult | undefined,
    patientId: string | undefined,
    status: ToDoStatus[],
    sort: 'asc' | 'desc',
    lastId?: string | undefined,
  ): Promise<ToDo[]> {
    let docs;
    if (lastId) {
      const last = await firstValueFrom(
        this.collection(dietitianId).doc(lastId).get(),
      );
      try {
        const snap = await firstValueFrom(
          this.collection(dietitianId, (ref) => {
            let qry = ref.orderBy('dateStart', sort).startAfter(last).limit(30);
            if (status) {
              qry = qry.where('status', 'in', status);
            }
            if (period) {
              if (period.start) {
                qry = qry.where('dateStart', '>=', period.start.toDate());
              }

              if (period.end) {
                qry = qry.where('dateStart', '>=', period.end.toDate());
              }
            }
            if (patientId) {
              qry = qry.where('target.id', '==', patientId);
            }
            return qry;
          }).get(),
        );
        docs = snap.docs;
      } catch (e) {
        console.error(e);
      }
    } else {
      try {
        const snap = await firstValueFrom(
          this.collection(dietitianId, (ref) => {
            let qry = ref.orderBy('dateStart', sort).limit(30);
            if (status) {
              qry = qry.where('status', 'in', status);
            }
            if (period) {
              if (period.start) {
                const d = period.start.toDate();
                d.setHours(0, 0, 0);
                qry = qry.where('dateStart', '>=', d);
              }
              if (period.end) {
                const d = period.end.toDate();
                d.setHours(23, 59, 59);
                qry = qry.where('dateStart', '<=', d);
              }
            }
            if (patientId) {
              qry = qry.where('target.id', '==', patientId);
            }
            return qry;
          }).get(),
        );
        docs = snap.docs;
      } catch (e) {
        console.error(e);
      }
    }
    const results: ToDo[] = [];
    if (docs && docs.length > 0) {
      for (const doc of docs) {
        results.push(
          await this.fromSchema(doc.data() as ToDoSchema, dietitianId, doc.id),
        );
      }
    }
    return results;
  }

  async countIsLate(dietitianId: string, date: Date): Promise<number> {
    date.setHours(0, 0, 0);
    const snap = await firstValueFrom(
      this.collection(dietitianId, (ref) => {
        return ref
          .where('dateStart', '<', date)
          .where('status', '==', 'ON_HOLD');
      }).get(),
    );
    return snap.docs.length;
  }

  async findAllLate(
    dietitianId: string,
    date: Date,
    sort: 'asc' | 'desc',
    lastId?: string | undefined,
  ): Promise<ToDo[]> {
    let docs;
    date.setHours(0, 0, 0);

    if (lastId) {
      const last = await firstValueFrom(
        this.collection(dietitianId).doc(lastId).get(),
      );
      const snap = await firstValueFrom(
        this.collection(dietitianId, (ref) => {
          return ref
            .where('dateStart', '<', date)
            .where('status', '==', 'ON_HOLD')
            .orderBy('dateStart', sort)
            .startAfter(last)
            .limit(30);
        }).get(),
      );
      docs = snap.docs;
    } else {
      const snap = await firstValueFrom(
        this.collection(dietitianId, (ref) => {
          return ref
            .where('dateStart', '<', date)
            .where('status', '==', 'ON_HOLD')
            .orderBy('dateStart', sort)
            .limit(30);
        }).get(),
      );
      docs = snap.docs;
    }

    const results: ToDo[] = [];
    if (docs) {
      for (const doc of docs) {
        results.push(
          await this.fromSchema(doc.data() as ToDoSchema, dietitianId, doc.id),
        );
      }
    }
    return results;
  }

  async countAtDate(
    dietitianId: string,
    start: Date,
    end: Date,
  ): Promise<number> {
    start.setHours(0, 0, 0);
    end.setHours(23, 59, 59);
    const snap = await firstValueFrom(
      this.collection(dietitianId, (ref) => {
        return ref
          .where('dateStart', '>=', start)
          .where('dateStart', '<=', end)
          .where('status', '==', 'ON_HOLD');
      }).get(),
    );
    return snap.docs.length;
  }

  async findAllInStatusAndDate(
    dietitianId: string,
    start: Date,
    end: Date,
    patientId: string | undefined,
    status: ToDoStatus[],
    sort: 'asc' | 'desc',
    lastId?: string | undefined,
  ): Promise<ToDo[]> {
    let docs;
    start.setHours(0, 0, 0);
    end.setHours(23, 59, 59);

    if (lastId) {
      const last = await firstValueFrom(
        this.collection(dietitianId).doc(lastId).get(),
      );
      const snap = await firstValueFrom(
        this.collection(dietitianId, (ref) => {
          let qry = ref
            .where('dateStart', '>=', start)
            .where('dateStart', '<=', end)
            .orderBy('dateStart', sort)
            .startAfter(last)
            .limit(30);
          if (status) {
            qry = qry.where('status', 'in', status);
          }
          if (patientId) {
            qry = qry.where('target.id', '==', patientId);
          }
          return qry;
        }).get(),
      );
      docs = snap.docs;
    } else {
      const snap = await firstValueFrom(
        this.collection(dietitianId, (ref) => {
          let qry = ref
            .where('dateStart', '>=', start)
            .where('dateStart', '<=', end)
            .orderBy('dateStart', sort)
            .limit(30);
          if (status) {
            qry = qry.where('status', 'in', status);
          }
          if (patientId) {
            qry = qry.where('target.id', '==', patientId);
          }
          return qry;
        }).get(),
      );
      docs = snap.docs;
    }

    const results: ToDo[] = [];
    if (docs) {
      for (const doc of docs) {
        results.push(
          await this.fromSchema(doc.data() as ToDoSchema, dietitianId, doc.id),
        );
      }
    }
    return results;
  }

  async findAllForPatients(
    dietitianId: string,
    status?: ToDoStatus,
  ): Promise<ToDo[]> {
    const results: ToDo[] = [];
    let docs;
    if (status) {
      const snap = await firstValueFrom(
        this.collection(dietitianId, (ref) =>
          ref.where('status', '==', status).where('target', '!=', null),
        ).get(),
      );
      docs = snap.docs;
    } else {
      const snap = await firstValueFrom(
        this.collection(dietitianId, (ref) =>
          ref.where('target', '!=', null),
        ).get(),
      );
      docs = snap.docs;
    }
    for (const doc of docs) {
      results.push(
        await this.fromSchema(doc.data() as ToDoSchema, dietitianId, doc.id),
      );
    }
    return results;
  }

  async create(dietitianId: string, toDo: ToDo): Promise<ToDo> {
    const schema = this.toSchema(toDo);
    schema.status = 'ON_HOLD';
    const created = await this.collection(dietitianId).add(schema);
    return this.fromSchema(schema, dietitianId, created.id);
  }

  async update(dietitianId: string, toDo: ToDo): Promise<ToDo> {
    const schema = this.toSchema(toDo);
    await this.collection(dietitianId).doc(toDo.id.toString()).set(schema);
    return this.fromSchema(schema, dietitianId, toDo.id.toString());
  }

  async delete(dietitianId: string, todo: ToDo): Promise<void> {
    await this.collection(dietitianId).doc(todo.id.toString()).delete();
  }
}
